import { ButtonHTMLAttributes, forwardRef, ReactElement } from 'react'
import { Slot, Slottable } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

const buttonVariants = cva(
  [
    'relative inline-flex items-center justify-center space-x-[1rem] px-[2rem] h-[4.4rem] w-auto rounded-[1.5rem]',
    'before:absolute before:top-0 before:left-0 before:w-full before:h-full before:rounded-[1.5rem]',
    'before:transition-transform before:duration-500 before:ease-out-back',
    'hover:before:scale-[1.05]',
    '[&>*]:relative [&>*]:inline-flex [&>*]:overflow-hidden',
    '[&>*>span]:transition-transform [&>*>span]:duration-300 [&>*>span]:ease-out-cubic',
    '[&>*>span]:hover:-translate-y-full',
    '[&>*]:after:content-[attr(data-label)] [&>*]:after:translate-y-full [&>*]:after:w-full [&>*]:after:h-full [&>*]:after:font-garet [&>*]:after:text-label [&>*]:after:uppercase [&>*]:after:absolute [&>*]:after:top-0 [&>*]:after:left-0',
    '[&>*]:after:transition-transform [&>*]:after:duration-300 [&>*]:after:ease-out-cubic',
    '[&>*]:hover:after:translate-y-0 [&>*]:disabled:after:hidden'
  ],
  {
    variants: {
      variant: {
        default: 'before:bg-mainbody text-background',
        primary: 'before:bg-primary text-white',
        secondary: 'before:bg-secondary text-primary',
        tertiary: 'before:bg-heading text-secondary',
        link: 'p-[1rem] -m-[1rem] h-auto'
      }
    },
    defaultVariants: {
      variant: 'default'
    }
  }
)

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
    startIcon?: ReactElement
  }

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { children, variant, startIcon, className, asChild = false, ...props },
    ref
  ) => {
    const Component = asChild ? Slot : 'button'

    return (
      <Component
        ref={ref}
        className={cn(buttonVariants({ variant, className }))}
        {...props}
      >
        {startIcon}
        <Slottable>{children}</Slottable>
      </Component>
    )
  }
)

export default Button
